/**
 * useAuth.js
 * Path: frontend/src/hooks/useAuth.js
 * Description: Hook operacji logowania i wylogowania.
 * Related: context/AuthContext.js, api/auth.js
 */

import { useContext, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { fetchProfile, loginRequest, setupPasswordRequest } from "../api/auth";

export function useAuth() {
  const { setToken, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [setupToken, setSetupToken] = useState("");
  const [setupEmail, setSetupEmail] = useState("");

  async function login(email, password) {
    setLoading(true);
    setError("");
    try {
      const response = await loginRequest(email, password);
      if (response.needsPasswordSetup) {
        setNeedsPasswordSetup(true);
        setSetupToken(response.setupToken || "");
        setSetupEmail(response.email || email);
        return response;
      }

      setToken(response.accessToken);
      setUser(response.user);
      setNeedsPasswordSetup(false);
      setSetupToken("");
      setSetupEmail("");
      return response;
    } catch (err) {
      const message = err?.response?.data?.error || "Nie udało się zalogować.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function finishPasswordSetup(password, confirmPassword) {
    setLoading(true);
    setError("");
    try {
      const response = await setupPasswordRequest(setupToken, password, confirmPassword);
      setToken(response.accessToken);
      setUser(response.user);
      setNeedsPasswordSetup(false);
      setSetupToken("");
      setSetupEmail("");
      return response;
    } catch (err) {
      const message = err?.response?.data?.error || "Nie udało się ustawić hasła.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function refreshProfile(token) {
    const profile = await fetchProfile(token);
    setUser(profile.user || null);
    return profile.user || null;
  }

  function logout() {
    setToken(null);
    setUser(null);
    setNeedsPasswordSetup(false);
    setSetupToken("");
    setSetupEmail("");
  }

  return {
    login,
    finishPasswordSetup,
    refreshProfile,
    logout,
    loading,
    error,
    needsPasswordSetup,
    setupEmail,
  };
}

